import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import FiatValue from 'components/FiatValue'
import Loader from 'components/Loader'
import { RowBetween, RowFixed } from 'components/Row'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { TruncatedText } from 'components/swap/styleds'
import { MouseoverTooltip } from 'components/Tooltip'
import { useTokenValue } from 'hooks/useTokenPrice'
import { useContext, useMemo } from 'react'
import { Text } from 'rebass'
import { ThemeContext } from 'styled-components'
import { Separator, ThemedText } from 'theme'

type RewardVaultStakeStepProps = {
  amount: CurrencyAmount<Token> | undefined
  onStake: () => void
  next: () => void
  rewardVault?: string
  rewardVaultRewardSymbol?: string
  apr?: number
} & Omit<ConfirmationTransactionStepProps, 'onConfirm' | 'children'>

const RewardVaultStakeStep = ({
  next,
  onStake,
  amount,
  attemptingTxn,
  rewardVault,
  rewardVaultRewardSymbol,
  apr,
  ...rest
}: RewardVaultStakeStepProps) => {
  const theme = useContext(ThemeContext)

  const amountValue = useTokenValue(amount)

  const estimatedYearlyRewards = useMemo(() => {
    if (!amountValue || !apr) return 0
    return (amountValue * apr) / 100
  }, [amountValue, apr])
  const rewardVaultLabel = rewardVaultRewardSymbol ? `${rewardVaultRewardSymbol} Reward Vault` : 'Reward Vault'

  return (
    <ConfirmationTransactionStep onConfirm={next} attemptingTxn={attemptingTxn} {...rest}>
      <Column width="100%" gap="4px" style={{ marginTop: '12px' }}>
        <LightCard padding="0.75rem 1rem">
          <AutoColumn gap="8px">
            <RowBetween align="center">
              <RowFixed gap="0px">
                <TruncatedText fontSize={24} fontWeight={500}>
                  {amount ? amount?.toExact() : <Loader />}
                </TruncatedText>
              </RowFixed>
              <RowFixed gap="0px">
                <CurrencyLogo currency={amount?.currency} size="20px" style={{ marginRight: '12px' }} />
                <Text fontSize={20} fontWeight={500}>
                  {amount?.currency.symbol}
                </Text>
              </RowFixed>
            </RowBetween>
            <RowBetween>
              <FiatValue currencyAmount={amount} />
            </RowBetween>
          </AutoColumn>
        </LightCard>

        <LightCard>
          <AutoColumn gap="8px">
            <RowBetween>
              <RowFixed>
                <MouseoverTooltip
                  text={<>Annual Percentage Rate for staking in the {rewardVaultLabel}</>}
                  disableHover={false}
                >
                  <ThemedText.DeprecatedSubHeader color={theme.deprecated_text1}>APR</ThemedText.DeprecatedSubHeader>
                </MouseoverTooltip>
              </RowFixed>
              <ThemedText.DeprecatedBlack textAlign="right" fontSize={14}>
                {apr?.toFixed(2)}%
              </ThemedText.DeprecatedBlack>
            </RowBetween>

            <Separator />

            <RowBetween>
              <RowFixed style={{ marginRight: '20px', alignItems: 'end' }}>
                <MouseoverTooltip
                  text={<>Estimated yearly earnings based on current APR and stake amount</>}
                  disableHover={false}
                >
                  <ThemedText.DeprecatedSubHeader color={theme.deprecated_text3}>
                    Estimated Yearly Rewards
                  </ThemedText.DeprecatedSubHeader>
                </MouseoverTooltip>
              </RowFixed>
              <ThemedText.DeprecatedBlack textAlign="right" fontSize={14} color={theme.deprecated_text3}>
                <FiatValue value={estimatedYearlyRewards || 0} />
              </ThemedText.DeprecatedBlack>
            </RowBetween>

            <RowBetween>
              <RowFixed>
                <MouseoverTooltip text={<>Rewards must be claimed separately from unstaking</>} disableHover={false}>
                  <ThemedText.DeprecatedSubHeader color={theme.deprecated_text1}>Note</ThemedText.DeprecatedSubHeader>
                </MouseoverTooltip>
              </RowFixed>
              <ThemedText.DeprecatedBlack textAlign="right" fontSize={14} color={theme.deprecated_text3}>
                Rewards must be claimed separately
              </ThemedText.DeprecatedBlack>
            </RowBetween>
          </AutoColumn>
        </LightCard>

        <ButtonPrimary
          style={{ margin: '20px 0 0 0' }}
          onClick={onStake}
          disabled={!amount || attemptingTxn || amount.equalTo('0')}
        >
          <Text fontWeight={500} fontSize={20}>
            <>Confirm Stake</>
          </Text>
        </ButtonPrimary>
      </Column>
    </ConfirmationTransactionStep>
  )
}

export default RewardVaultStakeStep
